import { supabase } from '../lib/supabase';

/**
 * Format currency to Turkish Lira
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })?.format(amount);
};

/**
 * Format large numbers to K/M notation
 */
export const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000)?.toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000)?.toFixed(1) + 'K';
  }
  return num?.toString();
};

/**
 * Create a new campaign
 */
export const createCampaign = async (campaignData) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Insert campaign
    const { data: campaign, error: campaignError } = await supabase?.from('campaigns')?.insert({
        artist_id: user?.id,
        name: campaignData?.name,
        description: campaignData?.description,
        budget: parseFloat(campaignData?.budget),
        start_date: campaignData?.startDate,
        end_date: campaignData?.endDate,
        goals: campaignData?.goals,
        status: campaignData?.status || 'draft',
        proposal_template: campaignData?.proposalTemplate,
        brand_guidelines: campaignData?.brandGuidelines,
        hashtag_requirements: campaignData?.hashtagRequirements
      })?.select()?.single();

    if (campaignError) {
      if (isSchemaError(campaignError)) {
        console.error('Schema error:', campaignError?.message);
        throw campaignError;
      }
      throw new Error(campaignError.message);
    }

    // Insert campaign influencers if provided
    if (campaignData?.selectedInfluencers && campaignData?.selectedInfluencers?.length > 0) {
      const campaignInfluencers = campaignData?.selectedInfluencers?.map(influencerId => ({
        campaign_id: campaign?.id,
        influencer_id: influencerId,
        allocated_budget: parseFloat(campaignData?.budget) / campaignData?.selectedInfluencers?.length,
        proposal_sent: false,
        proposal_accepted: false
      }));

      const { error: influencersError } = await supabase?.from('campaign_influencers')?.insert(campaignInfluencers);

      if (influencersError) {
        if (isSchemaError(influencersError)) {
          console.error('Schema error:', influencersError?.message);
          throw influencersError;
        }
        console.log('Error adding influencers:', influencersError?.message);
      }
    }

    return campaign;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

/**
 * Update existing campaign (for draft saving)
 */
export const updateCampaign = async (campaignId, campaignData) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase?.from('campaigns')?.update({
        name: campaignData?.name,
        description: campaignData?.description,
        budget: parseFloat(campaignData?.budget),
        start_date: campaignData?.startDate,
        end_date: campaignData?.endDate,
        goals: campaignData?.goals,
        status: campaignData?.status,
        proposal_template: campaignData?.proposalTemplate,
        brand_guidelines: campaignData?.brandGuidelines,
        hashtag_requirements: campaignData?.hashtagRequirements
      })?.eq('id', campaignId)?.eq('artist_id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }
};

/**
 * Fetch influencers with filters
 */
export const fetchInfluencers = async (filters = {}) => {
  try {
    let query = supabase?.from('influencers')?.select('*');

    // Apply filters
    if (filters?.minFollowers) {
      query = query?.gte('follower_count', filters?.minFollowers);
    }

    if (filters?.maxFollowers) {
      query = query?.lte('follower_count', filters?.maxFollowers);
    }

    if (filters?.minEngagement) {
      query = query?.gte('engagement_rate', filters?.minEngagement);
    }

    if (filters?.contentSpecialization && filters?.contentSpecialization?.length > 0) {
      query = query?.in('content_specialization', filters?.contentSpecialization);
    }

    if (filters?.availabilityStatus) {
      query = query?.eq('availability_status', filters?.availabilityStatus);
    }

    if (filters?.verified !== undefined) {
      query = query?.eq('verified', filters?.verified);
    }

    // Apply sorting
    query = query?.order('follower_count', { ascending: false });

    const { data, error } = await query;

    if (error) {
      if (error?.code === 'PGRST116') {
        return [];
      }
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching influencers:', error);
    throw error;
  }
};

/**
 * Fetch campaign by ID
 */
export const fetchCampaignById = async (campaignId) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase?.from('campaigns')?.select(`
        *,
        campaign_influencers(
          influencer_id,
          allocated_budget
        )
      `)?.eq('id', campaignId)?.eq('artist_id', user?.id)?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    throw error;
  }
};

/**
 * Check if error is schema-related
 */
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    
    if (errorClass === '42') {
      return true;
    }
    
    if (errorClass === '08') {
      return true;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}
